/*
 * Copyright (C) 2014 Spanning Cloud Apps.  All rights reserved.
 */

import countries from './countries';
import states from './states';

/**
 * Module used for attaching common constants.
 */
angular
  .module('sbsf.constants', [])
  /**
   * Constants tied to the location value set in the signed request at
   * sbsf.context.environment.displayLocation
   */
  .constant('SF_LOCATIONS', {
    CHATTER: 'ChatterFeed',
    VISUAL_FORCE: 'Visualforce'
  })
  /**
   * Used in error notification to specify some default about the message.
   */
  .constant('ALERT_EVENTS', {
    ERROR: {
      type: 'error',
      eventName: 'error_event'
    },
    SUCCESS: {
      type: 'success',
      eventName: 'success_event'
    },
    CLOSE: {
      type: 'close',
      eventName: 'close_event'
    },
    UNAUTHORIZED: {
      type: 'unauthorized',
      eventName: '401_event'
    },
    EXPIRED_ORG: {
      type: 'expired_org',
      eventName: 'expired_org_event'
    }
  })
  /**
   * Check box status.
   */
  .constant('CHECKED_STATUS', {
    ALL: 1,
    SOME: 2,
    NONE: 3
  })
  /**
   * Angular date formats. Uses these formatting codes: https://angular-ui.github.io/bootstrap/#!#dateparser
   */
  .constant(
    'NG_DATE_FORMATS',
    Object.freeze({
      SHORT: 'MMM d, yyyy',
      LONG: 'MMM d, yyyy h:mm a'
    })
  )
  /**
   * Momentjs date formats. Uses these formatting codes: https://momentjs.com/docs/#/displaying/format/
   */
  .constant(
    'MomentFormats',
    Object.freeze({
      BATCH: 'ddd, MMM D, YYYY hh:mm a', // Format for displaying backup and other batch dates.
      SHORT: 'MMM D, YYYY'
    })
  )
  /**
   * Defines the different possible restore states.
   */
  .constant('RESTORE_STATE', {
    NONE: 0,
    SUCCESS: 1,
    PARTIAL: 2,
    FAILURE: 3,
    RUNNING: 4
  })
  /**
   * Progress Actions.
   */
  .constant('PROGRESS_ACTIONS', {
    BACKUP: 1,
    EXPORT: 2,
    OBJECT_RESTORE: 3,
    BULK_RESTORE: 4,
    MAINTENANCE: 5,
    BULK_RESTORE_PREVIEW: 6,
    BULK_RESTORE_CSV_VERIFICATION: 7,
    METADATA_COMPARE: 8,
    RESTORE_SEARCH: 9,
    METADATA_RESTORE: 10,
    GDPR: 11,

    1: 'BACKUP',
    2: 'EXPORT',
    3: 'OBJECT_RESTORE',
    4: 'BULK_RESTORE',
    5: 'MAINTENANCE',
    6: 'BULK_RESTORE_PREVIEW',
    7: 'BULK_RESTORE_CSV_VERIFICATION',
    8: 'METADATA_COMPARE',
    9: 'RESTORE_SEARCH',
    10: 'METADATA_RESTORE',
    11: 'GDPR'
  })
  /**
   * Progress Bookmark Object.
   */
  .constant('PROGRESS_BOOKMARK_OBJECT', {
    COMPRESSING: 1,
    UPLOADING: 2,
    DOWNLOADING: 3,
    LOADING: 4,
    RUNNING: 5
  })
  /**
   * Values around the progress status.
   */
  .constant('PROGRESS_STATUS', {
    QUEUED: 0,
    LOADING: 1,
    RUNNING: 2,
    COMPLETE: 3,
    ERROR: 4,
    REQUEUED: 5,

    0: 'QUEUED',
    1: 'LOADING',
    2: 'RUNNING',
    3: 'COMPLETE',
    4: 'ERROR',
    5: 'REQUEUED'
  })
  /**
   * Constants for batch limits.
   */
  .constant('BATCH_LIMITS', {
    NUM_BATCHES_ON_DASHBOARD: 12,
    NUM_BATCHES_ON_DASHBOARD_MOBILE: 12,
    NUM_BATCHES_IN_BACKUP_TIME_CHART: 8,
    NUM_BATCHES_IN_BACKUP_COUNTS_CHART: 8
  })
  /**
   * Constants for batch range.
   */
  .constant('BATCH_RANGE', {
    UNBOUNDED: 8888888888888888
  })
  /**
   * Constants for batch results.
   */
  .constant('BATCH_RESULTS', {
    CANCELLED: 'CANCELLED',
    FAILURE: 'FAILURE',
    SUCCESS: 'SUCCESS',
    PARTIAL: 'PARTIAL'
  })
  /**
   *  Constants that define HTTP errors codes thrown from the rest layer.
   */
  .constant('HTTP_ERROR_STATUS', {
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    REFRESH_TOKEN_BACKUP_USER: 554,
    REFRESH_TOKEN_NON_BACKUP_USER: 555,
    TRIAL_EXPIRED: 556,
    SUBSCRIPTION_EXPIRED: 557,
    MAINTENANCE_MODE: 558
  })
  /**
   * Billing status constants for organizations.
   */
  .constant('BILLING_STATUS', {
    PAID: 'PAID',
    TRIAL: 'TRIAL'
  })
  /**
   * Customer types constants for organizations.
   */
  .constant('CUSTOMER_TYPES', {
    UNKNOWN: 'UNKNOWN',
    STRIPE: 'STRIPE',
    INVOICE: 'INVOICE'
  })
  /**
   * Constant related to CSS classes for different object types.
   */
  .constant('TYPES', {
    typeToCls: {
      Account: 'sca-icon-accounts',
      Contact: 'sca-icon-contacts',
      Contracts: 'sca-icon-contracts',
      Lead: 'sca-icon-leads',
      Opportunity: 'sca-icon-opportunities',
      Report: 'sca-icon-reports',
      Quote: 'sca-icon-quotes'
    }
  })
  /**
   * Mobile states
   */
  .constant('MOBILE_STATES', {
    dashboard: {
      order: 1,
      state: 'mobileMain.admin',
      title: 'DASHBOARD',
      iconPath: '/images/dashboard.svg'
    },
    activity: {
      order: 2,
      state: 'mobileActivity.logs',
      title: 'ACTIVITY',
      iconPath: '/images/active.svg'
    }
  })
  /**
   *  Used for data around delays for showing/hiding.
   */
  .constant('DELAYS', {
    HIDE_MOBILE_ALERT: 5000,
    SHOW_TRIAL_CIRCLE: 1500
  })
  /**
   * Object used to track mobile events.
   */
  .constant('MOBILE_EVENTS', {
    SHOW_MOBILE_ALERT: 'showMobileAlert',
    HEADER_CHANGE: 'headerChange',
    HEADER_REFRESH: 'headerRefreshClicked'
  })
  /**
   * Maps batch types to icon classes.
   */
  .constant('TYPE_ICON_CLASS', {
    FAILURE: 'sbsf-activity-red-x sca-icon-x-gray',
    SUCCESS: 'sbsf-activity-check-gray sca-icon-check-gray',
    PARTIAL: 'sbsf-activity-exclamation sca-icon-incomplete-gray',
    BULK_RESTORE: 'sbsf-activity-check-gray sca-icon-btn-restore-14-dark-gray'
  })
  /**
   * Maps mobile batch types to icon classes.
   */
  .constant('MOBILE_ICON_CLASS', {
    FAILURE: 'sca-icon-x-gray-r',
    SUCCESS: 'sca-icon-check-gray-r',
    PARTIAL: 'sca-icon-incomplete-gray-r'
  })
  /**
   * Result keys for batches to message keys.
   */
  .constant('RESULT_KEYS', {
    CANCELLED: 'CANCELLED',
    FAILURE: 'FAILED',
    SUCCESS: 'COMPLETED',
    PARTIAL: 'PARTIAL'
  })
  /**
   * Constants used for audit data types.
   */
  .constant('AUDIT_DATA_TYPES', {
    ADDED_OBJECT_TYPE_EXCLUSION: 'function',
    ADMINS_ONLY_CHANGED: 'boolean',
    ALERT_RULE_ADDED: 'alertruleadded',
    ALERT_RULE_CHANGED: 'alertrulechanged',
    ALERT_RULE_DELETED: 'alertruledeleted',
    API_LIMIT_CHANGED: 'apilimitchanged',
    BACKUP_START_TIME_CHANGED: 'function',
    BILLING_INFO_CHANGED: 'none',
    BULK_API_LIMIT_CHANGED: 'bulkapilimitchanged',
    CHATTER_NOTIFICATION_SETTINGS_CHANGED: 'function',
    DELETED_OBJECT_TYPE_EXCLUSION: 'function',
    EMAIL_NOTIFICATION_SETTINGS_CHANGED: 'function',
    SERVICE_ACCOUNT_CHANGED: 'serviceaccountchange',
    SUBSCRIPTION_DATES_CHANGED: 'date',
    SUBSCRIPTION_TYPE_CHANGED: 'string',
    USE_KMS_CHANGED: 'usekmschange',
    USER_PERMISSIONS_CHANGED: 'userpermissionschanged'
  })
  /**
   * Constants used for audit view types.
   */
  .constant('AUDIT_VIEW_TYPES', {
    CHATTER_NOTIFICATION_SETTINGS_CHANGED: 1,
    EMAIL_NOTIFICATION_SETTINGS_CHANGED: 2,
    BACKUP_START_TIME_CHANGED: 3,
    ADDED_OBJECT_TYPE_EXCLUSION: 4,
    DELETED_OBJECT_TYPE_EXCLUSION: 5
  })
  /**
   * Constant to define log types.
   */
  .constant('LOG_TYPE', {
    ACTIVITY: 'activity',
    AUDIT: 'audit'
  })
  /**
   * Batch state object.
   */
  .constant('BACKUP_STATE', {
    STARTED: 'STARTED',
    FINISHED: 'FINISHED'
  })
  /**
   * Email frequency object.
   */
  .constant('EMAIL_FREQUENCY', {
    DAILY: 'DAILY',
    WEEKLY: 'WEEKLY',
    NEVER: 'NONE'
  })
  /**
   * Chatter user type object.
   */
  .constant('CHATTER_USER_TYPE', {
    USERS: 'users',
    GROUPS: 'groups'
  })
  /**
   * Salesforce object field types
   */
  .constant('SALESFORCE_FIELD_TYPES', {
    BOOLEAN: 'boolean',
    DATE: 'date',
    DATETIME: 'datetime',
    DOUBLE: 'double',
    EMAIL: 'email',
    INT: 'int',
    MULTIPICKLIST: 'multipicklist',
    PICKLIST: 'picklist',
    STRING: 'string',
    TIME: 'time'
  })
  /**
   * Strategies for overwriting fields
   */
  .constant(
    'RESTORE_OVERWRITE_STRATEGIES',
    Object.freeze({
      CONSTANT: 'CONSTANT',
      RANDOM: 'RANDOM'
    })
  )
  /**
   * Event type for bulk restore
   */
  .constant('BULK_RESTORE_EVENTS', {
    COLLAPSE_ALL: 'collapse_all',
    OVERWRITE_VALUES_CHECKED: 'overwrite_values_checked',
    PAGING_EVENT: 'bulk_restore_page_event',
    SPECIFIC_FIELDS_CHECKED: 'specific_fields_checked'
  })
  /**
   * Key Codes for key press, key down, key up events
   */
  .constant('KEY_CODES', {
    ENTER: 13
  })
  /**
   * Sobject state.
   */
  .constant('SOBJECT_STATE', {
    UPDATED: 1,
    DELETED: 2,
    UPDATED_OR_DELETED: 3,
    NEW: 5,
    UNCHANGED: 6
  })
  /**
   * Bulk restore types.
   */
  .constant('BULK_RESTORE_TYPES', {
    OBJECT: 1,
    METADATA: 2
  })
  /**
   * Bulk restore destination types.
   */
  .constant('BULK_RESTORE_DEST_TYPES', {
    SAME_ORG: 1,
    CROSS_ORG: 2
  })
  .constant('BULK_RESTORE_CHILD_STRATEGIES', {
    GRANULAR: 1,
    CLASSIC: 2
  })
  /**
   * Metadata compare types.
   */
  .constant('METADATA_COMPARE_TYPES', {
    NONE: 0,
    SAME_ORG_COMPARE: 1,
    CROSS_ORG_COMPARE: 2
  })
  /**
   * Metadata compare same org batch.
   */
  .constant('METADATA_COMPARE_BATCH_TYPES', {
    NONE: 0,
    CURRENT: 1,
    ANOTHER_BATCH: 2
  })
  /**
   * Metadata compare state.
   */
  .constant('METADATA_COMPARE_STATE', {
    RIGHT_ONLY: 1,
    LEFT_ONLY: 2,
    DIFF: 3,
    EQUAL: 4
  })
  .constant('NON_RESTORABLE_EVENT_CODE', 114)
  /**
   * Error types
   */
  .constant('ERROR_TYPES', {
    100: {
      type: 'SPANNING_REST_API_LIMIT',
      description: 'SPANNING_REST_API_LIMIT_DESC'
    },
    101: {
      type: 'SALESFORCE_REST_API_LIMIT',
      description: 'SALESFORCE_REST_API_LIMIT_DESC'
    },
    102: {
      type: 'IMPLEMENTATION_RESTRICTION',
      description: 'IMPLEMENTATION_RESTRICTION_DESC'
    },
    103: {
      type: 'SALESFORCE_AUTHENTICATION_ERROR',
      description: 'SALESFORCE_AUTHENTICATION_ERROR_DESC'
    },
    104: {
      type: 'OBJECT_BACKUP_FAILURE',
      description: 'OBJECT_BACKUP_FAILURE_DESC'
    },
    105: {
      type: 'OBJECT_RESTORE_ERROR',
      description: 'OBJECT_RESTORE_ERROR_DESC'
    },
    106: {
      type: 'SALESFORCE_OBJECT_CREATE_ERROR',
      description: 'SALESFORCE_OBJECT_CREATE_ERROR_DESC'
    },
    107: {
      type: 'SALESFORCE_INSUFFICIENT_ACCESS',
      description: 'SALESFORCE_INSUFFICIENT_ACCESS_DESC'
    },
    108: {
      type: 'SALESFORCE_INACTIVE_USER',
      description: 'SALESFORCE_INACTIVE_USER_DESC'
    },
    109: {
      type: 'BATCH_OUTPUT_DOWNLOADED',
      description: 'BATCH_OUTPUT_DOWNLOADED_DESC'
    },
    110: {
      type: 'AWS_KMS_ERROR',
      description: 'AWS_KMS_ERROR_DESC'
    },
    111: {
      type: 'INSUFFICIENT_ACCESS_CROSS_REF',
      description: 'INSUFFICIENT_ACCESS_CROSS_REF_DESC'
    },
    112: {
      type: 'OUT_OF_STORAGE_SPACE',
      description: 'OUT_OF_STORAGE_SPACE_DESC'
    },
    113: {
      type: 'ENTITY_LOCKED',
      description: 'ENTITY_LOCKED_DESC'
    },
    114: {
      type: 'UNCREATABLE_FIELD',
      description: 'UNCREATABLE_FIELD_DESC'
    },
    115: {
      type: 'UNUPDATABLE_FIELD',
      description: 'UNUPDATABLE_FIELD_DESC'
    },
    116: {
      type: 'REQUIRED_FIELD_MISSING',
      description: 'REQUIRED_FIELD_MISSING_DESC'
    },
    117: {
      type: 'INACTIVE_RECORD_REFERENCE',
      description: 'INACTIVE_RECORD_REFERENCE_DESC'
    },
    119: {
      type: 'OPERATION_TIMED_OUT',
      description: 'OPERATION_TIMED_OUT_DESC'
    },
    120: {
      type: 'AUTH_INFO_NOT_FOUND',
      description: 'AUTH_INFO_NOT_FOUND_DESC'
    },
    121: {
      type: 'DATABASE_CONNECTIVITY_ERROR',
      description: 'DATABASE_CONNECTIVITY_ERROR_DESC'
    },
    122: {
      type: 'SALESFORCE_LAYOUT_NOT_FOUND',
      description: 'SALESFORCE_LAYOUT_NOT_FOUND_DESC'
    },
    123: {
      type: 'SALESFORCE_PARENT_NOT_FOUND',
      description: 'SALESFORCE_PARENT_NOT_FOUND_DESC'
    },
    124: {
      type: 'SALESFORCE_CANNOT_CREATE_WORKFLOW',
      description: 'SALESFORCE_CANNOT_CREATE_WORKFLOW_DESC'
    },
    125: {
      type: 'SALESFORCE_MISSING_META_XML_FILE',
      description: 'SALESFORCE_MISSING_META_XML_FILE_DESC'
    },
    126: {
      type: 'SALESFORCE_DASHBOARD_GRID_LAYOUT',
      description: 'SALESFORCE_DASHBOARD_GRID_LAYOUT_DESC'
    },
    127: {
      type: 'SALESFORCE_METADATA_DEPLOYMENT_ROLLBACK',
      description: 'SALESFORCE_METADATA_DEPLOYMENT_ROLLBACK_DESC'
    },
    128: {
      type: 'SALESFORCE_IN_FIELD_VALIDATION',
      description: 'SALESFORCE_IN_FIELD_VALIDATION_DESC'
    },
    129: {
      type: 'SALESFORCE_EDIT_INVALID_TAB',
      description: 'SALESFORCE_EDIT_INVALID_TAB_DESC'
    },
    130: {
      type: 'SALESFORCE_WRONG_NAMESPACE',
      description: 'SALESFORCE_WRONG_NAMESPACE_DESC'
    },
    131: {
      type: 'BULK_RESTORE_CANCELLED',
      description: 'BULK_RESTORE_CANCELLED_DESC'
    },
    132: {
      type: 'SALESFORCE_LIST_METADATA_ERROR',
      description: 'SALESFORCE_LIST_METADATA_ERROR'
    },
    134: {
      type: 'SPANNING_BULK_API_LIMIT',
      description: 'SPANNING_BULK_API_LIMIT_DESC'
    },
    135: {
      type: 'SALESFORCE_BULK_API_LIMIT',
      description: 'SALESFORCE_BULK_API_LIMIT_DESC'
    },
    136: {
      type: 'GDPR_NOT_FOUND_RECORD_VERSION_IN_CHUNK',
      description: 'GDPR_NOT_FOUND_RECORD_VERSION_IN_CHUNK_DESC'
    },
    137: {
      type: 'BATCH_CANCELLED',
      description: 'BATCH_CANCELLED_DESC'
    },
    138: {
      type: 'NO_ITEMS_FOUND_TO_RESTORE',
      description: 'NO_ITEMS_FOUND_TO_RESTORE_DESC'
    },
    500: {
      type: 'INTERNAL_ERROR',
      description: 'INTERNAL_ERROR_DESC'
    }
  })
  /**
   * The names of the states corresponding to all our views
   */
  .constant('VIEW_STATES', {
    ACCOUNT: 'accountMain',
    ADMIN: 'adminMain',
    ADMIN_HOME: 'adminMain.admin',
    ADMIN_START: 'adminMain.start',
    BATCH: 'batchMain',
    BATCH_HOME: 'batchMain.batch',
    BATCH_EXPORT: 'batchMain.export',
    BATCH_OBJECT_RESTORE: 'batchMain.objectRestore',
    BATCH_BULK_RESTORE: 'batchMain.bulkRestore',
    BATCH_GDPR_REQUEST: 'batchMain.gdprRequest',
    BATCH_METADATA_RESTORE: 'batchMain.metadataRestore',
    BULK_RESTORE: 'bulkRestore',
    BULK_RESTORE_ENTER: 'bulkRestore.enter',
    BULK_RESTORE_CONFIRM_METADATA: 'bulkRestore.metadataConfirmView',
    BULK_RESTORE_CONFIRM_OBJECT: 'bulkRestore.confirm',
    BULK_RESTORE_METADATA_SETTINGS: 'bulkRestore.metadataSettings',
    BULK_RESTORE_SELECT_CSV: 'bulkRestore.verifiedCsvSelectView',
    BULK_RESTORE_SELECT_DEST: 'bulkRestore.selectDestinationView',
    BULK_RESTORE_SELECT_METADATA: 'bulkRestore.metadataSelectView',
    BULK_RESTORE_SELECT_OBJECT: 'bulkRestore.selectview',
    BULK_RESTORE_SETTINGS: 'bulkRestore.settings',
    BULK_RESTORE_UPLOAD_CSV: 'bulkRestore.uploadView',
    BULK_EXPORT: 'bulkExport',
    BULK_EXPORT_ENTER: 'bulkExport.enter',
    BULK_EXPORT_CONFIRM_OBJECT: 'bulkExport.confirm',
    BULK_EXPORT_SELECT_CSV: 'bulkExport.verifiedCsvSelectView',
    BULK_EXPORT_SELECT_DEST: 'bulkExport.selectDestinationView',
    BULK_EXPORT_SELECT_OBJECT: 'bulkExport.selectview',
    BULK_EXPORT_SETTINGS: 'bulkExport.settings',
    BULK_EXPORT_UPLOAD_CSV: 'bulkExport.uploadView',
    CHATTER: 'chatterMain',
    CHATTER_ALERT_EVENT: 'chatterMain.alertEvent',
    CHATTER_API: 'chatterMain.api',
    CHATTER_BACKUP: 'chatterMain.backup',
    CHATTER_BULK_RESTORE: 'chatterMain.bulkRestore',
    CHATTER_BULK_RESTORE_CSV_VERIFICATION: 'chatterMain.bulkRestoreCsvVerification',
    CHATTER_EXPORT: 'chatterMain.export',
    CHATTER_METADATA_COMPARE: 'chatterMain.metadataCompare',
    CHATTER_METADATA_RESTORE: 'chatterMain.metadataRestore',
    CHATTER_GDPR_REQUEST: 'gdprRequest.enter',
    GDPR_REQUEST: 'gdprRequest',
    GDPR_REQUEST_ENTER: 'gdprRequest.enter',
    GDPR_REQUEST_SELECT_OPERATION: 'gdprRequest.selectOperation',
    GDPR_REQUEST_UPLOAD_CSV: 'gdprRequest.uploadView',
    INVALID_ACCESS: 'invalidAccess',
    INVALID_CONFIG: 'invalidConfig',
    LOG: 'logMain',
    LOG_HOME: 'logMain.log',
    MAINTENANCE_MODE: 'maintenanceMode',
    METADATA_COMPARE: 'metadataCompare',
    METADATA_COMPARE_RESULTS: 'metadataCompare.results',
    METADATA_COMPARE_SELECT: 'metadataCompare.select',
    METADATA_COMPARE_SELECT_COMP_TYPES: 'metadataCompare.selectComponentTypes',
    MOBILE: 'mobileMain',
    MOBILE_ACTIVITY: 'mobileActivity',
    MOBILE_ACTIVITY_LOGS: 'mobileActivity.logs',
    MOBILE_AUDIT_ITEM: 'mobileAuditItem',
    MOBILE_BATCH: 'mobileBatchMain',
    MOBILE_BATCH_ERROR: 'mobileBatchMainError',
    MOBILE_BATCH_EXPORT: 'mobileBatchMain.export',
    MOBILE_BATCH_HOME: 'mobileBatchMain.batch',
    MOBILE_BATCH_BULK_RESTORE: 'mobileBatchMain.bulkRestore',
    MOBILE_BATCH_OBJECT_RESTORE: 'mobileBatchMain.objectRestore',
    MOBILE_CHATTER: 'chatterAPI',
    MOBILE_EXPIRED: 'expiredLicenseMobile',
    MOBILE_HOME: 'mobileMain.admin',
    MOBILE_MENU: 'mobileMenu',
    MOBILE_START: 'mobileMain.start',
    MOBILE_SUPPORT: 'mobileSupport',
    MOBILE_SUPPORT_HOME: 'mobileSupport.support',
    RECEIPTS: 'receipts',
    RECEIPTS_CONTENT: 'receipts.content',
    RECEIPTS_CONTENT_RECEIPT: 'receipts.content.receipt',
    RECEIPTS_CONTENT_LIST: 'receipts.content.list',
    RESTORE: 'restoreMain',
    RESTORE_HOME: 'restoreMain.restore',
    RESTORE_DIFF: 'restoreMain.restore.diffviewer',
    RESTORE_CONFIRM: 'restoreMain.restore.confirm',
    RESTORE_REACT: 'restoreReact'
  })
  .constant('COUNTRIES', countries)
  .constant('USA_STATES', states)
  .constant('COUNTRY_CODES', {
    US: 'US'
  })
  .constant('SANDBOX_TYPE', {
    null: 'Not determined',
    0: 'Production',
    1: 'Sandbox'
  })
  .constant('API_TYPES', {
    REST_API: 'rest_api',
    BULK_API: 'bulk_api'
  })
  .constant('TIME_INTERVALS', {
    DAILY: 1,
    HOURLY: 2
  });
